// F8 (fase 2, corte 6) — los campos de la visita del contratista: un día
// y un rango de horas de ese día (inicio y término, en pasos de 30
// minutos; corte de ajustes, founder 2026-10-16: reemplaza a los bloques
// fijos). Los usan el formulario de asignar (opcional), el lote (opcional)
// y «Agendar visita» / «Cambiar visita» del panel. El día y las horas van
// en America/Santiago (horas de pared, sin zona): el día desde hoy hasta
// `OBSERVATION_VISIT_MAX_DAYS_AHEAD` días; el servidor valida igual. El DS
// no tiene un control de hora: `<input type="time" step="1800">` con la
// clase `.input` del formulario.
import { useId } from "react";
import { VISIT_SAME_AS_DECLINED_MESSAGE } from "@bitacorapro/api-types";
import { ApiError } from "../lib/api/error";
import {
  addCalendarDays,
  isVisitTime,
  OBSERVATION_VISIT_MAX_DAYS_AHEAD,
  OBSERVATION_VISIT_TIME_STEP_MINUTES,
  santiagoDate,
  visitRangeValid,
} from "@bitacorapro/core-engine";

export type VisitaDraft = { date: string; startTime: string; endTime: string };

export const VISITA_VACIA: VisitaDraft = { date: "", startTime: "", endTime: "" };

/** La ventana del selector de día, en Santiago. */
export function visitaWindow(now: Date = new Date()): { min: string; max: string } {
  const min = santiagoDate(now);
  return { min, max: addCalendarDays(min, OBSERVATION_VISIT_MAX_DAYS_AHEAD) };
}

/** Un `<input type="time">` puede entregar «09:00:00» (con segundos):
 *  la hora del wire es «09:00». */
function hhmm(t: string): string {
  return /^\d{2}:\d{2}:00$/.test(t) ? t.slice(0, 5) : t;
}

/**
 * Qué le falta al borrador para poder enviarse. Null = completa o vacía
 * (cuando es opcional). Un mensaje = el botón queda atenuado con ese motivo.
 */
export function faltaDeVisita(
  v: VisitaDraft,
  opts: { optional: boolean; now?: Date }
): string | null {
  const vacia = v.date === "" && v.startTime === "" && v.endTime === "";
  if (vacia) return opts.optional ? null : "Elige el día y el horario de la visita.";
  if (v.date === "") return "Elige el día de la visita.";
  if (v.startTime === "" || v.endTime === "") return "Elige la hora de inicio y la de término.";
  const inicio = hhmm(v.startTime);
  const termino = hhmm(v.endTime);
  if (!isVisitTime(inicio) || !isVisitTime(termino)) {
    return "Las horas van en bloques de 30 minutos (por ejemplo, 9:00 o 9:30).";
  }
  if (!visitRangeValid(inicio, termino))
    return "La hora de término tiene que ser después del inicio.";
  const { min, max } = visitaWindow(opts.now);
  if (v.date < min || v.date > max) return "El día tiene que ser hoy o uno próximo.";
  return null;
}

/**
 * El error de agendar en español. El servidor responde los VISIT_* con un
 * mensaje técnico en inglés: acá se traducen por código. Cualquier otro
 * error conserva su mensaje (o el `fallback`).
 */
export function visitaErrorMessage(
  err: unknown,
  words: { assigneeLower: string },
  fallback: string
): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "VISIT_STATUS_NOT_ELIGIBLE":
        return `La observación ya no está con el ${words.assigneeLower}. Recarga para ver su estado.`;
      case "VISIT_DATE_OUT_OF_RANGE":
        return `El día tiene que ser hoy o uno de los próximos ${OBSERVATION_VISIT_MAX_DAYS_AHEAD} días.`;
      case "VISIT_NOT_ENABLED":
        return "Esta observación no admite agendar una visita.";
      case "VISIT_ACTOR_NOT_ALLOWED":
        return "Tu rol no puede agendar visitas.";
      // ADR-0161 — el horario que el propietario pidió cambiar.
      case "VISIT_SAME_AS_DECLINED":
        return VISIT_SAME_AS_DECLINED_MESSAGE;
    }
  }
  return err instanceof Error ? err.message : fallback;
}

/** El borrador listo para el body, o undefined si no está completo. */
export function visitaBody(
  v: VisitaDraft
): { date: string; startTime: string; endTime: string } | undefined {
  return v.date !== "" && v.startTime !== "" && v.endTime !== ""
    ? { date: v.date, startTime: hhmm(v.startTime), endTime: hhmm(v.endTime) }
    : undefined;
}

export function VisitaFields({
  value,
  onChange,
  optional,
  elAssetParty,
  disabled = false,
}: {
  value: VisitaDraft;
  onChange: (v: VisitaDraft) => void;
  /** En asignar es opcional; en «Agendar visita», obligatoria. */
  optional: boolean;
  /** La contraparte con las palabras de la receta («el propietario»). */
  elAssetParty: string;
  disabled?: boolean;
}) {
  const dateId = useId();
  const startId = useId();
  const endId = useId();
  const hintId = useId();
  const { min, max } = visitaWindow();
  const step = OBSERVATION_VISIT_TIME_STEP_MINUTES * 60;
  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor={dateId}>
          {optional ? "Fecha de la visita (opcional)" : "Fecha de la visita"}
        </label>
        <input
          id={dateId}
          type="date"
          className="input"
          min={min}
          max={max}
          value={value.date}
          disabled={disabled}
          aria-describedby={hintId}
          onChange={(e) => onChange({ ...value, date: e.target.value })}
        />
      </div>
      <div className="grid-2">
        <div className="field">
          <label className="field__label" htmlFor={startId}>
            Desde
          </label>
          <input
            id={startId}
            type="time"
            step={step}
            className="input"
            value={value.startTime}
            disabled={disabled}
            aria-describedby={hintId}
            onChange={(e) => onChange({ ...value, startTime: e.target.value })}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor={endId}>
            Hasta
          </label>
          <input
            id={endId}
            type="time"
            step={step}
            className="input"
            value={value.endTime}
            disabled={disabled}
            aria-describedby={hintId}
            onChange={(e) => onChange({ ...value, endTime: e.target.value })}
          />
        </div>
      </div>
      <span className="field__hint" id={hintId}>
        {optional
          ? `Si ya la coordinaste, ${elAssetParty} verá el día y el horario en su observación.`
          : `${capitalizar(elAssetParty)} verá el día y el horario en su observación.`}
      </span>
    </>
  );
}

function capitalizar(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
